import { initProductCardSwatches } from './initProductCardSwatches';
import { createEkSlider, productSliderSwiperOverrides } from './ekSwiperSlider';

const STORAGE_KEY = 'ek:recently-viewed';
const SECTION_ATTR = 'data-recently-viewed-section';
const CARD_SECTION_NAME = 'ek-recently-viewed-card';

async function fetchCardHtml(handle: string): Promise<string | null> {
  try {
    const res = await fetch(`/products/${handle}?sections=${CARD_SECTION_NAME}`);
    if (!res.ok) return null;
    const data = await res.json();
    return (data as Record<string, string>)[CARD_SECTION_NAME] ?? null;
  } catch {
    return null;
  }
}

function buildItemWrapper(html: string, index: number, total: number): HTMLElement {
  const div = document.createElement('div');
  div.dataset.currentIndex = String(index);
  div.dataset.lastIndex = String(total);
  // Class names also appear in the migrated slider sections, so Tailwind
  // extracts them from Liquid even though this string lives in TS.
  div.className = 'swiper-slide w-[40%] max-w-[160px] md:w-[212px] md:max-w-[212px]';
  div.setAttribute('role', 'listitem');
  div.innerHTML = html;
  return div;
}

async function loadRecentlyViewed(sectionEl: HTMLElement): Promise<void> {
  const sectionId = sectionEl.dataset.sectionId;
  if (!sectionId) return;

  if (sectionEl.dataset.rvInit === 'true') return;
  sectionEl.dataset.rvInit = 'true';

  const currentHandle = sectionEl.dataset.currentProductHandle || '';
  const maxItems = parseInt(sectionEl.dataset.maxItems || '6', 10);

  let handles: string[] = [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    handles = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(handles)) handles = [];
  } catch {
    return;
  }

  if (currentHandle) {
    handles = handles.filter((h) => h !== currentHandle);
  }
  handles = handles.slice(0, maxItems);

  if (!handles.length) return;

  const htmlResults = await Promise.all(handles.map(fetchCardHtml));
  const validCards = htmlResults.filter((h): h is string => h !== null);

  if (!validCards.length) return;

  const itemsContainer = sectionEl.querySelector<HTMLElement>(`#recs-items-${sectionId}`);
  if (!itemsContainer) return;

  validCards.forEach((html, index) => {
    itemsContainer.appendChild(buildItemWrapper(html, index + 1, validCards.length));
  });

  const contentEl = sectionEl.querySelector<HTMLElement>('[data-rv-content]');
  if (contentEl) contentEl.style.display = '';
  sectionEl.style.removeProperty('min-height');
  sectionEl.style.removeProperty('overflow');

  // On desktop, compact layout when fewer than 6 cards (row does not fill the viewport):
  // - drop scroller pull-back padding/margin
  // - drop inner md:mx-10 + heading md:-ml-10 so width matches pre–mx-10 fix behavior
  const isCompact = validCards.length < 6 && window.matchMedia('(min-width: 768px)').matches;
  const scroller = sectionEl.querySelector<HTMLElement>(`#${CSS.escape(`recs-scroll-${sectionId}`)}`);
  if (isCompact) {
    if (scroller) {
      scroller.style.paddingLeft = '0';
      scroller.style.paddingRight = '0';
      scroller.style.marginLeft = '0';
      scroller.style.marginRight = '0';
    }
    const rvInner = sectionEl.querySelector<HTMLElement>('[data-rv-inner]');
    if (rvInner) {
      rvInner.style.marginLeft = '0';
      rvInner.style.marginRight = '0';
    }
    const rvHeading = sectionEl.querySelector<HTMLElement>('[data-rv-heading]');
    if (rvHeading) rvHeading.style.marginLeft = '0';
  }

  initProductCardSwatches(sectionEl);

  const sliderEl = sectionEl.querySelector<HTMLElement>('[data-product-recommendations-slider]');
  if (!sliderEl || !scroller) return;

  createEkSlider({
    sectionEl: sliderEl,
    sectionId,
    swiperEl: scroller,
    swiperOverrides: isCompact
      ? {
          ...productSliderSwiperOverrides,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          breakpoints: {
            768: { spaceBetween: 16, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
            1024: { spaceBetween: 16, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
          },
        }
      : productSliderSwiperOverrides,
  });
}

function observeSection(sectionEl: HTMLElement): void {
  const observer = new IntersectionObserver(
    (entries, obs) => {
      if (entries[0].isIntersecting) {
        obs.disconnect();
        loadRecentlyViewed(sectionEl);
      }
    },
    { rootMargin: '600px 0px' }
  );
  observer.observe(sectionEl);
}

export function initRecentlyViewedSlider(root: ParentNode = document): void {
  const sections = root.querySelectorAll<HTMLElement>(`[${SECTION_ATTR}]`);
  sections.forEach((sectionEl) => observeSection(sectionEl));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initRecentlyViewedSlider());
} else {
  initRecentlyViewedSlider();
}

document.addEventListener('shopify:section:load', (e: Event) => {
  const target = (e as CustomEvent).target as HTMLElement | null;
  if (!target) return;
  const sectionEl = target.querySelector<HTMLElement>(`[${SECTION_ATTR}]`);
  if (sectionEl) loadRecentlyViewed(sectionEl);
});
